import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { NotificationFilter, NotificationInbox } from "@/types/notifications";

const inboxKey = ["notifications", "inbox"] as const;
const countsKey = ["notifications", "counts"] as const;

const PAGE_SIZE = 20;

/** Invalidate both the paged inbox lists and the bell/rail unread counts. */
function invalidateInbox(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: inboxKey });
  void qc.invalidateQueries({ queryKey: countsKey });
}

/** Approving/rejecting a checkpoint or resolving a HITL step changes run
 *  state (unblocks/resumes or leaves it paused), so also refresh the Activity
 *  list + detail (prefix-matches `["maxwell", "runs", ...]`) — a parked run
 *  doesn't poll on its own, so the transition would otherwise go unseen until
 *  refresh. */
function invalidateInboxAndRuns(qc: ReturnType<typeof useQueryClient>) {
  invalidateInbox(qc);
  void qc.invalidateQueries({ queryKey: ["maxwell", "runs"] });
}

/**
 * Cursor-paged inbox for one filter. `next_cursor` from each page feeds the
 * next request's `before`, replacing the old 100-row cap with infinite scroll.
 * Each page also carries the authoritative `unread` / `needs_you` totals; read
 * them off the first page.
 */
export function useNotificationInbox(filter: NotificationFilter) {
  return useInfiniteQuery({
    queryKey: [...inboxKey, filter] as const,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ filter, limit: String(PAGE_SIZE) });
      if (pageParam) params.set("before", pageParam);
      return api.get<NotificationInbox>(`/api/notifications?${params.toString()}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    refetchInterval: 30_000,
  });
}

/**
 * Lightweight unread / needs-you counts for the bell, rail, and inbox-tab
 * badge. Hits the inbox endpoint with `limit=1` — the totals ride on every
 * response regardless of how many items come back, so this stays cheap.
 */
export function useNotificationCounts() {
  return useQuery({
    queryKey: countsKey,
    queryFn: () =>
      api.get<NotificationInbox>("/api/notifications?filter=unread&limit=1"),
    refetchInterval: 30_000,
    select: (d) => ({ unread: d.unread, needs_you: d.needs_you }),
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/api/notifications/${id}/read`),
    onSuccess: () => invalidateInbox(qc),
  });
}

/** Mark every unread notification read in one call (the catch-up button). */
export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>("/api/notifications/read-all"),
    onSuccess: () => invalidateInbox(qc),
  });
}

export function useApproveNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/api/notifications/${id}/approve`),
    onSuccess: () => invalidateInboxAndRuns(qc),
  });
}

export function useRejectNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/api/notifications/${id}/reject`),
    onSuccess: () => invalidateInboxAndRuns(qc),
  });
}

/** "Mark done" a human-in-the-loop step the run is paused on — records it done
 *  and resumes the run. */
export function useCompleteHitl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<void>(`/api/notifications/${id}/complete-hitl`),
    onSuccess: () => invalidateInboxAndRuns(qc),
  });
}

/** "Skip" a human-in-the-loop step — records it skipped and resumes the run so
 *  it proceeds to its remaining steps. */
export function useSkipHitl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/api/notifications/${id}/skip-hitl`),
    onSuccess: () => invalidateInboxAndRuns(qc),
  });
}
